import React, { useState, useCallback, createRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FORM_ERROR } from 'final-form';
import { Form } from '@vmos2/utils';
import { Button, Input, Field } from '@vmos2/design-system';
import { actionsNames, analyticsService } from '@vmos2/analytics';
import styled from '@emotion/styled';
import get from 'lodash/get';

import { useLocation } from './useLocation';
import { GeoLocationButton } from './geoLocationButton';
import { validate } from './validator';

type Props = {
  disableMyLocation?: boolean,
};

export const FinderByPostcode = ({ disableMyLocation }: Props): React$Node => {
  const { t } = useTranslation('geoLocation');
  const inputRef = createRef();

  const [error, setError] = useState(null);

  const {
    location,
    setLocation,
    setLocationByPostcode,
    geolocationKey,
    getNavigatorGeolocation,
    deniedAccess,
    setDeniedAccess,
    postCode,
  } = useLocation();

  const getLocation = useCallback(async ({ postCode }: { postCode: string }, form: function, complete: function) => {
    try {
      if (!postCode) {
        setLocation(null);
        throw new Error(t('wrongPostcode'));
      }
      await setLocationByPostcode(postCode);
      analyticsService.sendEvent(actionsNames.locationLoadedSuccess, { properties: { method: 'searchButton' } });
    } catch (e) {
      setError(e);
      return {
        [FORM_ERROR]: t('wrongPostcode'),
      };
    } finally {
      complete();
    }
    return true;
  }, []);

  if (!geolocationKey) return null;

  return (
    <Wrapper
      id="searchAddress"
      name="postcodeForm"
      onSubmit={getLocation}
      validate={values => validate(values, deniedAccess, error, t, location?.isNavigatorGeolocation)}
      initialValues={{ postCode }}
      render={({ handleSubmit, form, submitting }) => (
        <form onSubmit={handleSubmit}>
          <SearchForm>
            <FieldWrapper>
              <FormInput
                ref={inputRef}
                data-test="postCode-input"
                placeholder={get(location, 'isNavigatorGeolocation') ? t('currentLocation') : t('enterPostcode')}
                component={Input}
                activeGeolocation={!!get(location, 'isNavigatorGeolocation')}
                name="postCode"
                size="small"
                loading={submitting}
                parse={value => (value ? value.toUpperCase() : '')}
                showErrorOnBlur={false}
                handleChange={() => {
                  setDeniedAccess(null);
                  setError(null);
                }}
              />
            </FieldWrapper>
            <Button size="small" disabled={submitting} ml={4} data-test="submit-postcode-btn" type="submit" height="min-content">
              {t('search')}
            </Button>
          </SearchForm>
          <GeoLocationButton
            disableMyLocation={disableMyLocation}
            form={form}
            getNavigatorGeolocation={getNavigatorGeolocation}
          />
        </form>
      )}
    />
  );
};

const Wrapper = styled(Form)`
  display: inherit;
`;

const SearchForm = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  height: auto;
`;

const FieldWrapper = styled('div')`
  position: relative;
  flex: 1;
`;

const FormInput = styled(Field)`
  &::placeholder {
    color: ${({ activeGeolocation, theme }) => activeGeolocation && theme.color.brand};
  }
`;
